import { log } from "../utils/logs";
import { ConsentCheckResults, ConsentMetaData } from "../types";
import { topmostAccessibleWindow } from "../utils";

const TCF_API_VERSION: number = 2;

declare interface PingReturn {
  gdprApplies: boolean | undefined;
  cmpStatus: string;
  displayStatus: string;
}

declare interface TCData {
  listenerId: number | undefined;
  gdprApplies: boolean;
  eventStatus?: string;
  tcString: string;
  purpose?: {
    consents: {
      [key: number]: boolean;
    };
  };
}

// Sends the "ping" command to check CMP readiness and returns the result
async function pingCMP(): Promise<PingReturn> {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    // @ts-ignore
    topmostAccessibleWindow.__tcfapi(
      "ping",
      TCF_API_VERSION,
      (result: PingReturn) => {
        resolve(result);
      },
    );
  });
}

// Retrieves the TCData using __tcfapi once the CMP is ready
async function getTCData(): Promise<TCData> {
  return new Promise((resolve, reject) => {
    const callback = (tcData: TCData, success: boolean) => {
      if (success) {
        resolve(tcData);
      } else {
        reject("failed to get TCData");
      }
    };

    // eslint-disable-next-line
    // @ts-ignore
    topmostAccessibleWindow.__tcfapi(
      "addEventListener",
      TCF_API_VERSION,
      callback,
    );
  });
}

async function getConsent(): Promise<TCData> {
  if (typeof topmostAccessibleWindow?.__tcfapi !== "function") {
    throw new Error("tcfapi not found");
  }

  // Since we can't guarantee that __tcfapi is ready immediately,
  // call to the 'ping' command might help to avoid issues with uninitialized CMP states.
  const pingData = await pingCMP();

  // if gdprApplies is false on ping result return a TCData object with gdprApplies: false,
  // indicating that consent data isn’t required for this user.
  if (pingData.gdprApplies === false) {
    return { gdprApplies: false } as TCData;
  }

  // The cmpStatus property of PingResult object shall be one of the following:
  // - "stub": CMP not yet loaded
  // - "loaded": CMP is finished loading
  // - "error":	CMP is in an error state
  if (pingData && pingData.cmpStatus === "loaded") {
    // Verify display status
    // The displayStatus property of PingResult object shall be one of the following:
    // - "visible":	User interface is currently displayed
    // - "hidden": User interface is not yet or no longer displayed
    // - "disabled": User interface will not show (e.g. GDPR does not apply or TC data does not need renewal)
    if (pingData.displayStatus === "visible") {
      // assume consent not given yet, because consent message is currently displayed;
      return { gdprApplies: true, eventStatus: "cmpuishown" } as TCData;
    } else {
      return await getTCData();
    }
  } else {
    throw new Error(`CMP is not loaded - cmpStatus: ${pingData.cmpStatus}`);
  }
}

// The eventStatus property of the TCData object shall be one of the following:
// - "tcloaded": CMP is loaded and is prepared to surface a TC String to any calling scripts on the page -
// this event status going to be returned on each next page views after user interacted with consent message.
// - "useractioncomplete": whenever a user has confirmed or re-confirmed their choices -
// this event status going to be returned in the same page view after user interacted with consent message
// - "cmpuishown": the UI is surfaced or re-surfaced
const consentShownInCurrentPV = (tcData: TCData) => {
  if (tcData?.eventStatus === "tcloaded") {
    return false;
  }
  if (
    tcData?.eventStatus === "useractioncomplete" ||
    tcData?.eventStatus === "cmpuishown"
  ) {
    return true;
  }

  // if we fall here it means that tcData missed or has unexpected event status, this actually should never happen
  // but if so, we're going to hande this as if TCF API failed
  return false;
};

const isConsent = (tcData: TCData) => {
  if (!tcData) {
    // if tcData is not find, we can't determine whether or not the user has consented
    return false;
  }
  if (tcData.gdprApplies === false) {
    // if gdpr is not applies there no need to check consents
    return true;
  }

  return !!(
    !allFalse(tcData.purpose?.consents) &&
    isRequiredConsentGiven(tcData.purpose)
  );
};

const allFalse = (obj = {}) => !Object.values(obj).some(Boolean);

const isRequiredConsentGiven = (purpose) => {
  const requirePurposes = [1, 2, 3, 4, 7, 9, 10];
  return requirePurposes.every((key) => purpose?.consents?.[key]);
};

const processConsentData = (tcData: TCData): ConsentCheckResults => {
  const consentMeta: ConsentMetaData = {
    tcDataDefined: !!tcData,
    gdprApplies: tcData?.gdprApplies,
  };

  if (!tcData || tcData.gdprApplies === false) {
    // if no TC Data found or GDPR not applies it means that render is allowed regardless of consent given
    return {
      consentShownInCurrentPV: false,
      isConsentGiven: true,
      meta: consentMeta,
    } as ConsentCheckResults;
  }

  return {
    consentShownInCurrentPV: consentShownInCurrentPV(tcData),
    isConsentGiven: isConsent(tcData),
    meta: consentMeta,
  } as ConsentCheckResults;
};

// define if consent message appears.
export async function getConsentData() {
  return getConsent()
    .then((tcData: TCData) => {
      return processConsentData(tcData);
    })
    .catch((reason) => {
      log(
        "rlink.getConsent",
        {
          version: __SCRIPT_VERSION__,
          pageURL: topmostAccessibleWindow.location.href,
          error: reason instanceof Error ? reason.message : reason,
        },
        { severity: 3 },
      );

      // if __tcfapi not available RL message should be rendered assuming that consent given
      return {
        consentShownInCurrentPV: false,
        isConsentGiven: true,
        meta: {
          tcDataDefined: false,
        },
      } as ConsentCheckResults;
    });
}
